/**
 * Runtime configuration loaded from `/config.json` before the React tree
 * renders. Migrated from the previous `process.env.REACT_APP_API_URL` build-time
 * baking so that a single nginx image can serve every preview environment with
 * its own per-preview values (mounted via ConfigMap at
 * `/usr/share/nginx/html/config.json`).
 *
 * Build-time env vars that are NOT part of this runtime contract — Auth0
 * config, Sentry DSN, Stripe PK — stay baked at build time for MVP and continue
 * to flow through `process.env.REACT_APP_*`.
 */

export type RuntimeConfig = {
  apiUrl: string
  environment: string
  sentryRelease: string
  /**
   * LaunchDarkly client-side ID (ISSUE-2307). Required: feature flags orchestrate
   * functionality, so an environment that cannot evaluate them is misconfigured
   * rather than degraded — the loader fails loudly instead of silently serving
   * every flag as `false`.
   *
   * PUBLIC by design — served to the browser and visible in devtools. The
   * server-side SDK key must never appear here.
   */
  launchDarklyClientId: string
  /**
   * Serve the translations bundled in the image instead of fetching them from
   * the backend's `/translation/all` (which answers with what Weblate has
   * synced into the platform database).
   *
   * For PREVIEWS only, and enforced as such: a preview exists to show the code
   * on the branch, and its copy should be the copy that branch ships, not
   * whatever the shared database happens to hold. The dev and production apps
   * must keep fetching — the database is the source of truth there, and a
   * bundled fallback would quietly serve stale strings to real clients (the
   * mechanism behind the "reverted to Sponsored" class of bug).
   *
   * Off unless a preview's ConfigMap turns it on.
   */
  useBundledTranslations: boolean
  /**
   * Base URL of crm-integrations, which the onOffice iframe page calls to exchange its one-time code for the
   * session's bootstrap token. Optional: an environment without it (previews) shows the onOffice page's error.
   */
  crmIntegrationsUrl?: string
}

let runtimeConfig: RuntimeConfig | undefined

/**
 * Returns the loaded runtime config or throws when accessed before
 * `loadRuntimeConfig()` has resolved. Callers must ensure the bootstrap
 * (in `src/index.tsx`) has awaited the loader before any code reading the
 * config runs.
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (!runtimeConfig) {
    throw new Error(
      'Runtime config accessed before loadRuntimeConfig() resolved. ' +
      'Ensure the application bootstrap awaits the loader before rendering.'
    )
  }
  return runtimeConfig
}

const CONFIG_URL = '/config.json'

/**
 * Preview environments are named `preview-<previewName>` by the chart
 * (`frontend-platform.runtimeConfig`), and nothing else is. Dev and production
 * carry their own names, so an exact prefix match is the whole test.
 */
export const isPreviewEnvironment = (environment: string): boolean =>
  environment.startsWith('preview-')

// Bound the bootstrap fetch so a hung connection (degraded nginx, network
// black-hole) surfaces as a thrown error instead of leaving the user with a
// permanently blank page that emits no Sentry event.
const CONFIG_FETCH_TIMEOUT_MS = 10_000

/**
 * Validates `apiUrl` against an allow-list of schemes. The ConfigMap that
 * supplies this value is operator-controlled, so this is a privilege-
 * escalation guard (not a remote-attacker one): anyone with `update` on the
 * ConfigMap could otherwise rewrite it to a `javascript:` / `data:` URI or a
 * phishing host. We accept same-origin relative paths starting with `/` and
 * absolute http(s) URLs — everything else is rejected.
 */
function isValidApiUrl(value: string): boolean {
  if (value.startsWith('/') && !value.startsWith('//')) return true
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch {
    return false
  }
}

/**
 * Fetches `/config.json` from the same origin and stores the result in the
 * module-level singleton. Idempotent: subsequent calls return the cached
 * value without re-fetching.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (runtimeConfig) return runtimeConfig

  let response: Response
  try {
    response = await fetch(CONFIG_URL, {
      cache: 'no-store',
      signal: AbortSignal.timeout(CONFIG_FETCH_TIMEOUT_MS),
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'TimeoutError') {
      throw new Error(
        `Failed to load ${CONFIG_URL}: timed out after ${CONFIG_FETCH_TIMEOUT_MS}ms`,
        { cause: error }
      )
    }
    throw error
  }
  if (!response.ok) {
    throw new Error(
      `Failed to load ${CONFIG_URL}: ${response.status} ${response.statusText}`
    )
  }

  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.toLowerCase().includes('json')) {
    // A misconfigured proxy or a missing-file fallback to the SPA can return
    // index.html with a 200 — surface that explicitly rather than letting
    // `response.json()` throw an opaque SyntaxError.
    throw new Error(
      `Invalid ${CONFIG_URL}: expected JSON content-type, got "${contentType}"`
    )
  }

  let parsed: unknown
  try {
    parsed = await response.json()
  } catch (error) {
    throw new Error(`Invalid ${CONFIG_URL}: not parseable as JSON`, { cause: error })
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(`Invalid ${CONFIG_URL}: expected a JSON object`)
  }

  const candidate = parsed as Partial<RuntimeConfig>

  if (typeof candidate.apiUrl !== 'string' || !candidate.apiUrl) {
    throw new Error(`Invalid ${CONFIG_URL}: missing required field \`apiUrl\``)
  }
  if (!isValidApiUrl(candidate.apiUrl)) {
    throw new Error(
      `Invalid ${CONFIG_URL}: \`apiUrl\` must be a relative path (/...) ` +
      'or an absolute http(s) URL'
    )
  }

  const launchDarklyClientId = typeof candidate.launchDarklyClientId === 'string'
    ? candidate.launchDarklyClientId.trim()
    : ''
  if (!launchDarklyClientId) {
    throw new Error(
      `Invalid ${CONFIG_URL}: missing required field \`launchDarklyClientId\``
    )
  }

  const environment = typeof candidate.environment === 'string' ? candidate.environment : 'unknown'

  // Same scheme guard as apiUrl; an invalid value is dropped rather than failing the whole app for one page.
  const crmIntegrationsUrl = typeof candidate.crmIntegrationsUrl === 'string' &&
    isValidApiUrl(candidate.crmIntegrationsUrl.trim())
    ? candidate.crmIntegrationsUrl.trim()
    : undefined
  if (candidate.crmIntegrationsUrl !== undefined && crmIntegrationsUrl === undefined) {
    console.warn('[runtimeConfig] ignoring invalid crmIntegrationsUrl')
  }

  runtimeConfig = {
    apiUrl: candidate.apiUrl,
    environment,
    sentryRelease: typeof candidate.sentryRelease === 'string' ? candidate.sentryRelease : 'unknown',
    launchDarklyClientId,
    // Honoured only where it is meant to apply. The chart already refuses to
    // emit the field outside a preview; this is the second lock, on the side
    // that would suffer — a `true` reaching dev or production is ignored and
    // said out loud rather than silently freezing everyone's copy.
    useBundledTranslations: candidate.useBundledTranslations === true && isPreviewEnvironment(environment),
    crmIntegrationsUrl,
  }

  if (candidate.useBundledTranslations === true && !runtimeConfig.useBundledTranslations) {
    console.warn(
      `[runtimeConfig] ignoring useBundledTranslations in environment "${environment}" — previews only`
    )
  }

  // Use console here (not utils/logging) — this loader runs before the
  // logger module is imported. The breadcrumb is enough to triage broken
  // previews (which environment / which release).
  console.info(
    '[runtimeConfig] loaded',
    { environment: runtimeConfig.environment, sentryRelease: runtimeConfig.sentryRelease }
  )

  return runtimeConfig
}

/**
 * Test-only helper for seeding the singleton in unit tests so consumers
 * (`apiURL.ts` and friends) can be exercised without going through `fetch`.
 */
export function __setRuntimeConfigForTesting(config: RuntimeConfig | undefined): void {
  runtimeConfig = config
}
